/**
 * Portal Notifications Panel
 * Reads from portal_notifications table with read/unread tracking and priority.
 * Role-scoped via RLS (user_id = auth.uid()).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { usePortalRole } from "@/hooks/usePortalRole";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Bell, BellOff, AlertTriangle, CheckCircle2, Clock, Briefcase,
  Users, Loader2, Check, ShieldAlert, BarChart3, Flag, Trash2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "@/hooks/use-toast";
import AdminAlertResolution from "@/components/portal/AdminAlertResolution";
import {
  playNotificationSound,
  isNotificationSoundMuted,
  setNotificationSoundMuted,
} from "@/lib/notificationSound";

interface Notification {
  id: string;
  type: string;
  title: string;
  detail: string | null;
  severity: string;
  priority: string;
  created_at: string;
  read: boolean;
  href: string | null;
  source_alert_id?: string | null;
  /** Live state of the linked admin alert, when there is one. */
  alert_state?: "active" | "resolved" | "unknown";
}

const SECURITY_KEYWORDS = [
  "security", "mfa", "password", "suspicious", "unauthorised", "unauthorized",
  "data breach", "login", "auth", "permission", "role", "admin access",
];

function isSecurityCriticalNotification(notification: Pick<Notification, "type" | "title" | "detail" | "severity">) {
  if (notification.severity === "critical" || notification.type === "dispute") return true;
  const searchableText = `${notification.title} ${notification.detail ?? ""}`.toLowerCase();
  return SECURITY_KEYWORDS.some((keyword) => searchableText.includes(keyword));
}

const TYPE_ICON: Record<string, React.ElementType> = {
  assignment: Users,
  job_update: Briefcase,
  dispute: ShieldAlert,
  admin_alert: AlertTriangle,
  admin_action: Flag,
  ops_summary: BarChart3,
  new_lead: Bell,
};

const SEVERITY_ICON: Record<string, React.ElementType> = {
  info: CheckCircle2,
  warning: Clock,
  critical: AlertTriangle,
};

const PRIORITY_STYLES: Record<string, { border: string; bg: string; dot: string }> = {
  high: {
    border: "border-l-2 border-l-destructive",
    bg: "bg-destructive/[0.04]",
    dot: "bg-destructive",
  },
  normal: {
    border: "",
    bg: "bg-primary/[0.02]",
    dot: "bg-primary",
  },
  low: {
    border: "",
    bg: "",
    dot: "bg-muted-foreground",
  },
};

const SEVERITY_COLOR: Record<string, string> = {
  info: "text-primary",
  warning: "text-amber-500",
  critical: "text-destructive",
};

/**
 * Reroute notification hrefs to surfaces the active role can actually open.
 * For introducer_manager we redirect admin-only or consultant-only deep links
 * to the safest equivalent Introducer Manager list page.
 */
function resolveSafeHref(href: string, role: string | null | undefined): string {
  if (!href) return "/portal";
  if (role !== "introducer_manager") return href;

  // Lead detail (consultant/admin-only) -> introducer-sourced leads list
  if (/^\/portal\/(consultant|admin)\/leads\//.test(href)) {
    return "/portal/introducer-manager/leads";
  }
  // Admin recruitment surfaces -> manager applicants
  if (href.startsWith("/portal/admin/recruitment") || href.startsWith("/portal/admin/installer-recruitment")) {
    return "/portal/introducer-manager/applicants";
  }
  // Admin introducer intelligence -> manager performance
  if (href.startsWith("/portal/admin/introducer-intelligence")) {
    return "/portal/introducer-manager/performance";
  }
  // Other /portal/admin/* deep links the manager cannot enter -> manager dashboard
  if (href.startsWith("/portal/admin")) {
    return "/portal/introducer-manager";
  }
  return href;
}

/** Urgent = unread, high priority, and not backed by an alert that is already resolved. */
export function isUrgentNotification(n: Pick<Notification, "read" | "priority" | "alert_state">) {
  return !n.read && n.priority === "high" && n.alert_state !== "resolved";
}

const HISTORICAL_MS = 14 * 24 * 60 * 60 * 1000;

export default function PortalNotificationsPanel() {
  const { userId, role } = usePortalRole();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<Notification | null>(null);
  const [open, setOpen] = useState(false);
  const [bgUnread, setBgUnread] = useState(0);
  const [muted, setMuted] = useState<boolean>(() => isNotificationSoundMuted());
  const prevUnreadRef = useRef<number | null>(null);
  const firstLoadRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from("portal_notifications")
      .select("id, type, title, detail, severity, priority, created_at, read, href, source_alert_id")
      .eq("user_id", userId)
      .is("dismissed_at", null)
      .eq("is_test", false)
      .order("created_at", { ascending: false })
      .limit(40);
    if (error) {
      setLoadError(error.message);
      setLoading(false);
      return;
    }
    setLoadError(null);

    const rows = ((data as Notification[]) ?? []);
    // Read the live state of the underlying alert so a resolved alert is never
    // shown or counted as urgent, and an active one is labelled as such.
    const alertIds = [...new Set(rows.map((r) => r.source_alert_id).filter(Boolean))] as string[];
    if (alertIds.length) {
      const { data: alerts } = await supabase.from("admin_alerts").select("id, resolved_at").in("id", alertIds);
      const state = new Map((alerts ?? []).map((a) => [a.id, a.resolved_at ? "resolved" : "active"] as const));
      rows.forEach((r) => { if (r.source_alert_id) r.alert_state = state.get(r.source_alert_id) ?? "unknown"; });
    }

    // Sort: unread high-priority first, then unread normal, then read
    const sorted = rows.sort((a, b) => {
      const prioOrder: Record<string, number> = { high: 0, normal: 1, low: 2 };
      // Unread before read
      if (a.read !== b.read) return a.read ? 1 : -1;
      // By priority within same read state
      const pa = prioOrder[a.priority] ?? 1;
      const pb = prioOrder[b.priority] ?? 1;
      if (pa !== pb) return pa - pb;
      // By date within same priority
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    setNotifications(sorted);
    setLoading(false);
  }, [userId]);

  // Fetch on open
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Background poll for unread count every 30s.
  // Plays a soft chime when the unread count increases (skips first load).
  useEffect(() => {
    if (!userId) return;
    const poll = async () => {
      const { count } = await supabase
        .from("portal_notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("read", false)
        .is("dismissed_at", null)
        .eq("is_test", false);
      if (typeof count !== "number") return;

      const prev = prevUnreadRef.current;
      prevUnreadRef.current = count;
      setBgUnread(count);

      if (firstLoadRef.current) {
        firstLoadRef.current = false;
        return;
      }
      if (prev !== null && count > prev) {
        void playNotificationSound();
      }
    };
    poll();
    const interval = setInterval(poll, 30_000);
    return () => clearInterval(interval);
  }, [userId]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setNotificationSoundMuted(next);
    // Play a confirmation chime when unmuting (also satisfies iOS gesture requirement).
    if (!next) void playNotificationSound();
  };

  const markAllRead = async () => {
    if (!userId) return;
    const { error } = await supabase.rpc("mark_notifications_read", { p_user_id: userId });
    if (error) {
      toast({ title: "Notifications were not marked as read", description: error.message, variant: "destructive" });
      void fetchNotifications();
      return;
    }
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setBgUnread(0);
  };

  const dismissOne = async (id: string) => {
    const dismissedAt = new Date().toISOString();
    setNotifications(prev => prev.filter(n => n.id !== id));
    setBgUnread(prev => Math.max(0, prev - (notifications.find(n => n.id === id && !n.read) ? 1 : 0)));
    const { data, error } = await (supabase.from("portal_notifications") as any)
      .update({ dismissed_at: dismissedAt, read: true, read_at: dismissedAt })
      .eq("id", id)
      .select("id");
    if (error || !data || data.length === 0) {
      // Re-fetch on failure (including a silent zero-row update) so UI matches DB
      toast({
        title: "Notification was not dismissed",
        description: error?.message ?? "It could not be updated. Please refresh and try again.",
        variant: "destructive",
      });
      void fetchNotifications();
    }
  };

  const clearAllNonCritical = async () => {
    if (!userId) return;
    const dismissedAt = new Date().toISOString();
    const clearableIds = notifications.filter(n => !isSecurityCriticalNotification(n)).map(n => n.id);
    if (clearableIds.length === 0) {
      toast({ title: "No non-critical notifications to clear" });
      return;
    }
    setNotifications(prev => prev.filter(n => isSecurityCriticalNotification(n)));
    setBgUnread(notifications.filter(n => !n.read && isSecurityCriticalNotification(n)).length);
    const { error } = await (supabase.from("portal_notifications") as any)
      .update({ dismissed_at: dismissedAt, read: true, read_at: dismissedAt })
      .in("id", clearableIds)
      .eq("user_id", userId)
      .is("dismissed_at", null)
      .eq("is_test", false)
      .not("severity", "eq", "critical")
      .neq("type", "dispute");
    if (error) {
      toast({ title: "Notifications were not cleared", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Non-critical notifications cleared", description: "Security-critical alerts were left in place." });
    }
    void fetchNotifications();
  };

  const panelUnread = notifications.filter((n) => !n.read).length;
  const unreadCount = open ? panelUnread : Math.max(panelUnread, bgUnread);
  const highCount = notifications.filter(isUrgentNotification).length;

  return (
    <TooltipProvider>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative min-h-[44px] min-w-[44px] p-2" aria-label="Notifications">
          <Bell className={`w-4.5 h-4.5 ${highCount > 0 ? "text-destructive" : "text-muted-foreground"}`} />
          {unreadCount > 0 && (
            <span className={`absolute -top-0.5 -right-0.5 rounded-full text-[10px] font-bold flex items-center justify-center min-w-[18px] min-h-[18px] px-1 ${
              highCount > 0
                ? "bg-destructive text-destructive-foreground animate-pulse"
                : "bg-destructive text-destructive-foreground"
            }`}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(94vw,28rem)] p-0 max-h-[85vh] flex flex-col" align="end" sideOffset={8}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm text-foreground">Notifications</h3>
            {highCount > 0 && (
              <Badge variant="destructive" className="text-[10px] px-1.5 gap-0.5">
                <AlertTriangle className="w-2.5 h-2.5" />
                {highCount} urgent
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[10px] text-muted-foreground"
              onClick={toggleMute}
              aria-label={muted ? "Unmute notification sound" : "Mute notification sound"}
              title={muted ? "Sound off — tap to enable" : "Sound on — tap to mute"}
            >
              {muted ? <BellOff className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
            </Button>
          </div>
        </div>
        {notifications.length > 0 && (
          <div className="grid gap-2 border-b px-4 py-3 sm:grid-cols-2">
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px] justify-center text-xs"
              onClick={markAllRead}
              title="Marks visible notifications as read; does not remove them."
            >
              <Check className="w-3 h-3" /> Mark all as read
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="min-h-[44px] justify-center whitespace-normal text-center text-xs leading-tight"
              onClick={clearAllNonCritical}
              title="Dismisses visible non-critical notifications. Security-critical alerts stay visible."
            >
              <Trash2 className="w-3 h-3" /> Clear all non-critical notifications
            </Button>
          </div>
        )}
        {/* Radix ScrollArea wraps content in a display:table div that grows to fit
            long, truncated text — this pushed each row's Dismiss button outside the
            visible popover, so items (notably long critical alerts) could not be cleared.
            Forcing block layout keeps rows within the popover width. */}
        <ScrollArea className="flex-1 max-h-[calc(85vh-9rem)] min-h-[18rem] [&_[data-radix-scroll-area-viewport]>div]:!block">
          {loadError ? (
            <div role="alert" className="space-y-2 px-4 py-6 text-center text-sm">
              <p>Notifications could not be loaded: {loadError}</p>
              <Button size="sm" variant="outline" className="min-h-[44px]" onClick={() => void fetchNotifications()}>Try again</Button>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">All clear — no notifications.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((n) => {
                const Icon = TYPE_ICON[n.type] ?? SEVERITY_ICON[n.severity] ?? CheckCircle2;
                const color = SEVERITY_COLOR[n.severity] ?? "text-primary";
                const prio = PRIORITY_STYLES[n.priority] ?? PRIORITY_STYLES.normal;

                return (
                  <div
                    key={n.id}
                    className={`px-4 py-3 flex items-start gap-3 transition-colors ${prio.border} ${
                      !n.read ? prio.bg : ""
                    } ${(n.href || n.type === "admin_alert") ? "cursor-pointer hover:bg-muted/50" : ""}`}
                    onClick={() => {
                      const href = n.href ?? (n.type === "admin_alert" ? "/portal/admin/system-health" : null);
                      if (!href) return;
                      const target = resolveSafeHref(href, role);
                      setOpen(false);
                      if (target.startsWith("http://") || target.startsWith("https://")) {
                        window.location.href = target;
                      } else {
                        navigate(target);
                      }
                    }}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className={`text-sm leading-tight flex-1 ${!n.read ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                          {n.title}
                        </p>
                        {isUrgentNotification(n) && (
                          <Badge variant="destructive" className="text-[9px] px-1 py-0 h-4 shrink-0">
                            Urgent
                          </Badge>
                        )}
                      </div>
                      {n.detail && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-3 break-words" title={n.detail}>{n.detail}</p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                        <span>{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</span>
                        {n.alert_state === "active" && <Badge variant="outline" className="h-4 px-1 text-[9px]" data-testid="alert-state">Alert still open</Badge>}
                        {n.alert_state === "resolved" && <Badge variant="secondary" className="h-4 px-1 text-[9px]" data-testid="alert-state">Alert resolved</Badge>}
                        {Date.now() - new Date(n.created_at).getTime() > HISTORICAL_MS && <span>· older item</span>}
                      </div>
                      {n.alert_state === "active" && role === "admin" && (
                        <Button variant="link" size="sm" className="h-auto min-h-[36px] px-0 text-xs"
                          onClick={(e) => { e.stopPropagation(); setResolving(n); }}>
                          Resolve alert with a note…
                        </Button>
                      )}
                    </div>
                    {!n.read && (
                      <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${prio.dot}`} />
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-[44px] shrink-0 px-3 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissOne(n.id);
                          }}
                          aria-label="Hide this notification from my list"
                        >
                          Hide
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {n.alert_state === "active"
                          ? "Hides it from your list only. The alert itself stays open until it is resolved with a note."
                          : "Hides it from your list. The record is kept for audit."}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
    {resolving?.source_alert_id && (
      <AdminAlertResolution
        alertId={resolving.source_alert_id}
        alertTitle={resolving.title}
        open
        onOpenChange={(o) => { if (!o) setResolving(null); }}
        onResolved={() => { setResolving(null); void fetchNotifications(); }}
      />
    )}
    </TooltipProvider>
  );
}
